import { styled } from '@mui/material';
import { type FC, useEffect } from 'react';

import { useIsMounted } from '../../../hooks/dom/useIsMounted';
import { Icon } from '../../assets/icons/Icon';
import { BlankButton } from '../../controls/Button/BlankButton';
import { Text } from '../../typography/Text';
import { VisuallyHidden } from '../../utilities/VisuallyHidden';

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
}));

const StyledButton = styled(BlankButton)(({ theme }) => ({
  ...theme.typography.m,
  color: theme.palette.brand.blue,
  borderRadius: 4,

  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',

  '&:hover': { textDecoration: 'underline' },
  '&:focus-visible': theme.mixins.focusRing,
}));

type LanguageSelectorProps = {
  locale: string;
  setLocale: (lang: string) => void;
};

export const LanguageSelector: FC<LanguageSelectorProps> = ({ locale, setLocale }) => {
  useEffect(() => {
    document.documentElement.setAttribute('lang', locale.toLowerCase());
  }, [locale]);

  const isMounted = useIsMounted();

  if (!isMounted) return null;

  return (
    <Wrapper>
      <Icon type="GlobeIcon" />
      <StyledButton
        type="button"
        onClick={() => setLocale('DE')}
        lang="de"
        aria-pressed={locale === 'DE'}
        data-testid="language-button"
      >
        <Text color={locale === 'DE' ? 'grey500' : 'carbon'} variant="s">
          <span aria-hidden="true">DEU</span> <VisuallyHidden>Deutsch</VisuallyHidden>
        </Text>
      </StyledButton>
      <StyledButton
        type="button"
        onClick={() => setLocale('EN')}
        lang="en"
        aria-pressed={locale === 'EN'}
        data-testid="language-button"
      >
        <Text color={locale === 'EN' ? 'grey500' : 'carbon'} variant="s">
          <span aria-hidden="true">ENG</span> <VisuallyHidden>English</VisuallyHidden>
        </Text>
      </StyledButton>
    </Wrapper>
  );
};
